import {
    composeSelector,
    ContextWithQuery,
    ContextWithTranslatedQuery,
    extractSuggestionsPipeline,
    HasType,
    PipelineExecutors,
    SelectorFn,
    SelectorFns
} from "@me8eon/agent";
import { executeLlmPipelineDetails } from "@me8eon/llm_agents";
import { AiClients, AiToolsRepository } from "@me8eon/aiclient";
import { llmSelector, TranslateSelector, translateToEnglishSelector } from "@me8eon/llm_selector";
import { KnnToolImplGenerator } from "@me8eon/ai.tools.elasticsearch";
import { JqlToolImplGenerator } from "@me8eon/ai.tools.jira";
import { GetUserOrdersToolImplGenerator } from "@me8eon/ai.tools.service_api/src/get_user_orders.function.tool";
import { GetUserIncidentsToolImplGenerator } from "@me8eon/ai.tools.service_api/src/get_user_incidents.function.tool";
import { SearchPeopleToolImplGenerator, SearchManagerToolImplGenerator, SearchTeamToolImplGenerator } from "@me8eon/ai.tools.graph_api";
import { ElasticSearchConfig } from "@me8eon/ai.tools.utils";
import { DisplayNexthinkActionToolImpl } from "@me8eon/ai.tools.nextthink/src/displayNexthinkAction.tool";
import { FetchNexthinkActionsToolImpl } from "@me8eon/ai.tools.nextthink/src/findNexthinkAction.tool";
import { DisplayIntuneActionToolImpl, FetchIntuneActionsToolImpl } from "@me8eon/ai.tools.intune";
import { AddDeviceToEntraGroupToolImpl } from "@me8eon/ai.tools.graph";
import {
    HybridSemanticSearchToolImpl
} from "@me8eon/ai.tools.elasticsearch/src/performHybridSemanticSearch.function.tool";
import { DisplayCreateIncidentActionToolImpl } from "@me8eon/ai.tools.service_api/src/displayCreateIncidentAction.tool";
import { DisplayLiveAgentChatActionToolImpl } from "@me8eon/ai.tools.service_api/src/displayLiveAgentChatAction.tool";
import { DisplayRequestCallbackActionToolImpl } from "@me8eon/ai.tools.service_api/src/displayRequestCallbackAction.tool";
import { appendTextToMessagePipeline } from "@me8eon/agent/src/pipelines/appendTextToMessagePipeline";
import { FetchAllPhonesDetailsList } from "@me8eon/ai.tools.order_it/src/fetchPhonesList.function.tool";
import { FetchAllTabletsDetailsList } from "@me8eon/ai.tools.order_it/src/fetchTabletsList.function.tool";
import { FetchAllLaptopsDetailsList } from "@me8eon/ai.tools.order_it/src/fetchLaptopsList.function.tool";
import { FetchAllPcsDetailsList } from "@me8eon/ai.tools.order_it/src/fetchPcsList.function.tool";
import { JwtTokenFn } from "@me8eon/authentication";
import { DisplayTeamsActionToolImpl } from "@me8eon/ai.tools.truedem_api/src/displayTeamsAction.tool";
import { DeepSearchToolImpl, EonKnowledgeBaseSearchToolImpl } from "@me8eon/ai.tools.eonknowledgeagent";
import { MyGeniusSearchToolImpl } from "@me8eon/ai.tools.mygeniusagent";

type ToolsRepositoryProps = {
    elasticSearchConfig: ElasticSearchConfig
    jiraTokenFn: () => Promise<string>
    graphApiTokenFn: JwtTokenFn
    trueDemApiTokenFn: JwtTokenFn
    jwtTokenFn: () => Promise<string | undefined>
    userEmail?: string  // Optional - used for ACL-enabled tools
}

export function aiToolsRepository({
                                      elasticSearchConfig,
                                      jiraTokenFn,
                                      graphApiTokenFn,
                                      trueDemApiTokenFn,
                                      jwtTokenFn,
                                      userEmail
                                  }: ToolsRepositoryProps): AiToolsRepository {
    return {
        knn: KnnToolImplGenerator(elasticSearchConfig, jwtTokenFn),
        semanticHybridSearch: HybridSemanticSearchToolImpl(elasticSearchConfig, jwtTokenFn),
        deepSearch: DeepSearchToolImpl(elasticSearchConfig, jwtTokenFn),
        jql: JqlToolImplGenerator(jiraTokenFn),
        get_user_orders: GetUserOrdersToolImplGenerator(jwtTokenFn),
        get_user_incidents: GetUserIncidentsToolImplGenerator(jwtTokenFn),
        search_people: SearchPeopleToolImplGenerator(graphApiTokenFn),
        search_manager: SearchManagerToolImplGenerator(graphApiTokenFn),
        search_team: SearchTeamToolImplGenerator(graphApiTokenFn),
        display_nexthink_action: DisplayNexthinkActionToolImpl(elasticSearchConfig, jwtTokenFn),
        display_create_incident_action: DisplayCreateIncidentActionToolImpl(),
        display_live_agent_chat_action: DisplayLiveAgentChatActionToolImpl(),
        display_request_callback_action: DisplayRequestCallbackActionToolImpl(),
        add_device_to_entra_group: AddDeviceToEntraGroupToolImpl(jwtTokenFn),
        fetch_nexthink_actions: FetchNexthinkActionsToolImpl(elasticSearchConfig, jwtTokenFn),
        fetch_intune_actions: FetchIntuneActionsToolImpl(elasticSearchConfig, jwtTokenFn),
        display_intune_action: DisplayIntuneActionToolImpl(elasticSearchConfig, jwtTokenFn),
        fetch_all_phones_details_list: FetchAllPhonesDetailsList(elasticSearchConfig, jwtTokenFn),
        fetch_all_tablets_details_list: FetchAllTabletsDetailsList(elasticSearchConfig, jwtTokenFn),
        fetch_all_laptops_details_list: FetchAllLaptopsDetailsList(elasticSearchConfig, jwtTokenFn),
        fetch_all_pcs_details_list: FetchAllPcsDetailsList(elasticSearchConfig, jwtTokenFn),
        display_teams_action: DisplayTeamsActionToolImpl(trueDemApiTokenFn, jwtTokenFn),
        eonKnowledgeBaseSearch: EonKnowledgeBaseSearchToolImpl(elasticSearchConfig, jwtTokenFn),
        myGeniusSearch: MyGeniusSearchToolImpl(elasticSearchConfig, jwtTokenFn),
    };
}

export const allExecutors = <Context extends ContextWithQuery>(aiClients: AiClients, toolRepo: AiToolsRepository): PipelineExecutors<Context> => {
    return {
        llm: executeLlmPipelineDetails(aiClients, toolRepo),
        extractSuggestions: extractSuggestionsPipeline(),
        appendTextToMessage: appendTextToMessagePipeline(),
    };
};

export function allSelectors<Context extends ContextWithQuery & ContextWithTranslatedQuery, Pipelines extends TranslateSelector, Selector extends HasType>(aiClients: AiClients, toolRepo: AiToolsRepository) {
    const llmSel = llmSelector<Context, Pipelines>(aiClients, toolRepo);

    const translateSelector: SelectorFn<Context, Pipelines> = translateToEnglishSelector(aiClients);
    const allSelectors: SelectorFns<Context> = {
        llm: llmSel,
        translate: translateSelector,
        compose: composeSelector<Context, Selector>(),
    };
    return allSelectors;
}